import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  searchCiCompetitors,
  addCiCompetitor,
  removeCiCompetitor,
  requestCiCompetitor,
  getCiCompetitors,
} from "../../api/competitiveIntelligence";
import { Skeleton } from "../shared/Skeleton";
import { ErrorState } from "../shared/StateMessage";
import useDrawerDialog from "../../hooks/useDrawerDialog";
import { categoryLabel, titleCaseName } from "../../utils/ciFormat";
import CompetitorSettingsPanel from "./CompetitorSettingsPanel";

// CompetitorPicker — slide-out search + add/remove + request UI for the CI
// competitor roster (spec §5.3, to-do B5 parts 1–7).
//
//   ✅ Debounced `ci_search_competitors` (B5 §1)
//   ✅ Filter chips rendered from `facets` — no hardcoded property types or
//      ownership classes (B5 §2). Counts are scoped by distance + pricing
//      only, so they stay stable while chips toggle.
//   ✅ Results list with Add / Remove buttons (B5 §5)
//   ✅ Show everything by default — no pre-filtering (B5 §3)
//   ✅ Request form when `no_results` is true (B5 §4)
//   ✅ Two-layer error handling per C4 / B5 §6:
//        Read RPC  → Postgres error  → generic "Couldn't load" (never raw)
//        Write RPC → data.error      → render user-facing text as-is
//   ✅ `limit_reached` + `upgrade_options` from add responses (B5 §6)
//   ✅ `config.client_can_manage: false` hides all add/remove/request
//      controls (B5 §6)
//   ✅ Settings panel via `ci_get_competitor_settings` (B5 §7)

const DEBOUNCE_MS = 350;

const fmtMiles = (value) => (value == null ? "—" : `${Number(value).toFixed(1)} mi`);
const fmtRating = (value) => (value == null ? "—" : Number(value).toFixed(1));

// Facet chip groups we render. Keys map to the `facets` object returned by
// `ci_search_competitors`; the param keys map to the RPC's filter params.
// We do NOT enumerate values here — they come entirely from the response so a
// new property type or ownership class appears without a code change (B5 §2).
const FACET_GROUPS = [
  { facetKey: "property_type",   paramKey: "propertyTypes",   label: "Property type" },
  { facetKey: "ownership_class", paramKey: "ownershipClasses", label: "Ownership" },
  { facetKey: "category",        paramKey: "categories",        label: "Category" },
];

// Property-type facet display helpers (to-do B5 / Item 5). The backend sends
// raw DB values with underscores (e.g. `glamping_host`); the UI shows title-
// cased labels (e.g. `Glamping Host`) with the count in parentheses.
// `sortPropertyTypes` keeps Campground first (the default filter from B4),
// then alphabetical. Facets may arrive as strings or `{ value, count }`
// objects — `propertyTypeEntryValue` tolerates both (see FacetChips).
const formatPropertyTypeLabel = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ""))
    .join(" ")
    .trim();
};

const propertyTypeEntryValue = (entry) =>
  typeof entry === "object" && entry ? entry.value : entry;

const sortPropertyTypes = (values) => {
  if (!Array.isArray(values)) return values;
  return [...values].sort((a, b) => {
    const ka = String(propertyTypeEntryValue(a) ?? "").toLowerCase();
    const kb = String(propertyTypeEntryValue(b) ?? "").toLowerCase();
    if (ka === "campground" && kb !== "campground") return -1;
    if (kb === "campground" && ka !== "campground") return 1;
    return ka.localeCompare(kb);
  });
};

// Toggle a value inside an array filter. Returns a new array (immutable) so
// React state updates trigger re-renders. `null`/empty means "all" — we never
// send an empty array to the RPC (see searchCiCompetitors).
const toggleInArray = (arr, value) => {
  if (!arr) return [value];
  if (arr.includes(value)) {
    const next = arr.filter((v) => v !== value);
    return next.length ? next : null;
  }
  return [...arr, value];
};

const FacetChips = ({ group, values, selected, onToggle, disabled }) => {
  // Property types: sort Campground first, then alphabetical (to-do B5/Item 5).
  const isPropertyType = group.paramKey === "propertyTypes";
  const sorted = isPropertyType ? sortPropertyTypes(values) : values;
  if (!Array.isArray(sorted) || sorted.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
        {group.label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((entry) => {
          // Facets may arrive as strings or as { value, count } objects —
          // tolerate both so we don't break if the backend tightens the shape.
          const value = typeof entry === "object" && entry ? entry.value : entry;
          const count = typeof entry === "object" && entry ? entry.count : null;
          if (value == null) return null;
          const active = Array.isArray(selected) && selected.includes(value);
          const label =
            group.paramKey === "categories" ? categoryLabel(value)
            : isPropertyType ? formatPropertyTypeLabel(value)
            : String(value);
          return (
            <button
              key={String(value)}
              type="button"
              disabled={disabled}
              onClick={() => onToggle(group.paramKey, value)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                active
                  ? "border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF]"
                  : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {label}
              {count != null && (
                <span className={`ml-1.5 tabular-nums ${active ? "text-[#32B1FF]/70" : "text-gray-400 dark:text-gray-500"}`}>
                  {isPropertyType ? `(${count})` : count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ResultRow = ({ result, onRoster, onAdd, onRemove, writePending, clientCanManage }) => {
  const isOnRoster = !!onRoster;
  const location = [result.city, result.state].filter(Boolean).join(", ");
  // Title-case ALL CAPS competitor names from Atlas and expose the full
  // display name as a hover tooltip for truncated long names (to-do B6).
  const rawName = result.park_name || result.name || result.competitor_name || "";
  const displayName = rawName ? titleCaseName(rawName) : "—";
  return (
    <li className="flex items-start justify-between gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={rawName ? displayName : undefined}>
          {displayName}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-x-2 gap-y-0.5 flex-wrap">
          {result.google_rating != null && (
            <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <svg viewBox="0 0 24 24" className="w-3 h-3 text-amber-500" fill="currentColor" aria-hidden="true">
                <path d="M12 2l2.9 6.93 7.1.62-5.4 4.74L18.18 22 12 18.27 5.82 22l1.58-7.71L2 9.55l7.1-.62L12 2z" />
              </svg>
              <span className="font-medium tabular-nums">{fmtRating(result.google_rating)}</span>
            </span>
          )}
          {result.distance_miles != null && (
            <>
              <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
              <span>{fmtMiles(result.distance_miles)}</span>
            </>
          )}
          {location && (
            <>
              <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
              <span>{location}</span>
            </>
          )}
          {result.property_type && (
            <>
              <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
              <span className="capitalize">{result.property_type}</span>
            </>
          )}
        </div>
      </div>
      <div className="shrink-0">
        {/* B5 §6: hide add/remove controls entirely when the plan doesn't allow
            self-serve management. The row still renders as a browse view. */}
        {clientCanManage && (
          isOnRoster ? (
            <button
              type="button"
              disabled={writePending}
              onClick={() => onRemove(result)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {writePending ? "Removing…" : "Remove"}
            </button>
          ) : (
            <button
              type="button"
              disabled={writePending}
              onClick={() => onAdd(result)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[#32B1FF] bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {writePending ? "Adding…" : "Add"}
            </button>
          )
        )}
      </div>
    </li>
  );
};

// RequestForm — shown when `ci_search_competitors` returns `no_results` (B5 §4).
// Lets the client ask us to track a property that isn't in the atlas. Calls the
// `ci_request_competitor` WRITE RPC, which returns { success, error, message }
// inside `data`. Error handling follows C4 / B5 §6: a Postgres-level error
// rejects (generic message); a user-facing failure resolves with data.error
// rendered verbatim.
//
// The form pre-fills `requested_name` and `search_term` from the current
// search string so the client doesn't retype what they just searched for.
const RequestForm = ({ clientId, searchTerm, onSubmitted, clientCanManage }) => {
  const [form, setForm] = useState({
    requestedName: searchTerm || "",
    city: "",
    state: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { kind, text }

  // Keep the name field in sync with the search term when it changes from
  // outside (e.g. the user keeps typing after no_results appeared). Only
  // pre-fill if the field is empty or still matches the previous search term —
  // don't clobber edits the user already made.
  const lastSearchRef = useRef(searchTerm);
  useEffect(() => {
    if (searchTerm !== lastSearchRef.current) {
      lastSearchRef.current = searchTerm;
      setForm((prev) => {
        if (prev.requestedName === "" || prev.requestedName === lastSearchRef.current) {
          return { ...prev, requestedName: searchTerm };
        }
        return prev;
      });
    }
  }, [searchTerm]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFeedback(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.requestedName.trim()) {
      setFeedback({ kind: "error", text: "Enter a property name to request." });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const resp = await requestCiCompetitor(clientId, {
        requestedName: form.requestedName.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        website: form.website.trim() || null,
        searchTerm: searchTerm || form.requestedName.trim(),
      });
      const payload = resp?.data ?? {};
      if (!payload.success) {
        // User-facing failure — render data.error verbatim (B5 §6).
        setFeedback({ kind: "error", text: payload.error || payload.message || "Couldn't submit request." });
      } else {
        setFeedback({ kind: "success", text: payload.message || "Request submitted. We'll review and add it." });
        onSubmitted?.();
      }
    } catch (err) {
      // Postgres-level error — generic message, never raw (C4).
      setFeedback({ kind: "error", text: "Couldn't submit request. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientCanManage) {
    // B5 §6: when the plan doesn't allow self-serve management, hide the
    // request form too — the client can't initiate changes.
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Your plan doesn't include self-serve competitor management. Contact your account manager to request a new property.
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40 focus:border-[#32B1FF]";

  return (
    <form onSubmit={handleSubmit} className="space-y-3" aria-label="Request a competitor">
      <div className="text-sm text-gray-700 dark:text-gray-200 font-medium">
        Can't find it? Request tracking for a property not yet in the atlas.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-1">
            Property name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.requestedName}
            onChange={handleChange("requestedName")}
            placeholder="e.g. Distant Drums RV Resort"
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-1">City</label>
          <input type="text" value={form.city} onChange={handleChange("city")} placeholder="Camp Verde" className={inputClass} />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-1">State</label>
          <input type="text" value={form.state} onChange={handleChange("state")} placeholder="AZ" className={inputClass} />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-1">Website</label>
          <input type="url" value={form.website} onChange={handleChange("website")} placeholder="https://…" className={inputClass} />
        </div>
      </div>
      {feedback && (
        <div
          role="status"
          className={`text-xs rounded-lg px-3 py-2 ${
            feedback.kind === "success"
              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
              : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
          }`}
        >
          {feedback.text}
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-[#32B1FF] text-white hover:bg-[#259ae6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Submitting…" : "Submit request"}
        </button>
      </div>
    </form>
  );
};

// UpgradeNotice — shown when an add response carries `limit_reached: true`
// with an `upgrade_options` array (B5 §6). Renders the user-facing error text
// from the backend plus a compact list of upgrade tiers. Clicking "View
// settings" opens the settings panel where the full plan cards live.
const UpgradeNotice = ({ errorText, upgradeOptions, onViewSettings }) => {
  const options = Array.isArray(upgradeOptions) ? upgradeOptions : [];
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5 space-y-2">
      <div className="text-xs text-amber-800 dark:text-amber-300">
        {errorText || "You've reached your plan's competitor limit."}
      </div>
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt, idx) => {
            const tier = opt?.plan || opt?.tier || opt?.plan_tier || `option-${idx}`;
            const max = opt?.max_competitors ?? opt?.competitor_limit;
            return (
              <span
                key={tier}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300"
              >
                <span className="font-semibold capitalize">{String(tier).replace(/_/g, " ")}</span>
                {max != null && <span className="tabular-nums">· {max} comps</span>}
              </span>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={onViewSettings}
        className="text-[11px] font-medium text-amber-800 dark:text-amber-300 underline hover:no-underline"
      >
        View settings
      </button>
    </div>
  );
};

const CompetitorPicker = ({ open, onClose, clientId, onRosterChanged }) => {
  const containerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const searchInputRef = useRef(null);
  useDrawerDialog(open, onClose);

  // Search state. `search` is the controlled input value; `debouncedSearch`
  // is what we actually send to the RPC. Property types default to campground
  // (to-do B4); ownership and category filters start empty (= "all") per B5 §3.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({
    propertyTypes: ["campground"],
    ownershipClasses: null,
    categories: null,
  });
  const [state, setState] = useState({ loading: false, error: "", data: null });
  // Roster of atlas_park_id → competitor, so result rows know whether to show
  // Add or Remove. Refreshed on open and after every successful write.
  const [rosterIds, setRosterIds] = useState(new Set());
  const [rosterLoading, setRosterLoading] = useState(false);
  // Per-result write status keyed by atlas_park_id, so individual rows show
  // pending state without blocking the whole list.
  const [writePending, setWritePending] = useState({});
  // User-facing write feedback (success or data.error from write RPCs).
  const [writeToast, setWriteToast] = useState(null);
  // `limit_reached` payload from an add response — carries `upgrade_options`
  // for upsell (B5 §6). Cleared on the next successful write or on close.
  const [limitReached, setLimitReached] = useState(null);
  // Settings panel visibility (B5 §7).
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Reset transient state whenever the drawer opens.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setDebouncedSearch("");
    setFilters({ propertyTypes: ["campground"], ownershipClasses: null, categories: null });
    setWriteToast(null);
    setWritePending({});
    setLimitReached(null);
    setSettingsOpen(false);
  }, [open]);

  // Debounce the search input. We don't fire on every keystroke — the RPC does
  // real work (trigram search across the atlas) and 350ms is the house debounce
  // used elsewhere in CI (matches RateChangeFeedPanel's filter input).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  // Load the current roster so result rows can show Add vs Remove. We only
  // need the atlas_park_id set, not the full rows.
  const refreshRoster = useCallback(() => {
    if (!clientId) return Promise.resolve();
    setRosterLoading(true);
    return getCiCompetitors(clientId)
      .then((resp) => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        setRosterIds(new Set(rows.map((r) => r.atlas_park_id).filter(Boolean)));
      })
      .catch(() => {
        // Non-fatal — rows just default to "Add". The search itself will
        // surface its own error if the backend is down.
        setRosterIds(new Set());
      })
      .finally(() => setRosterLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (open) refreshRoster();
  }, [open, refreshRoster]);

  // Run the search. Re-fires on debounce tick, filter change, or roster
  // refresh (so a just-added competitor shows as "on roster" in results).
  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    searchCiCompetitors(clientId, {
      search: debouncedSearch,
      propertyTypes: filters.propertyTypes,
      ownershipClasses: filters.ownershipClasses,
      categories: filters.categories,
      // B5 §3 — show everything by default. We intentionally do NOT default
      // pricingOnly to true; that would hide untrackable properties and is
      // the operator's call, not ours.
      pricingOnly: null,
      maxDistanceMiles: null,
    })
      .then((resp) => {
        if (cancelled) return;
        // ci_search_competitors returns { results, result_count, no_results,
        // facets, config }. Read RPC — a Postgres error rejects via ciRpc and
        // hits the .catch below (C4).
        const payload = resp?.data ?? {};
        setState({ loading: false, error: "", data: payload });
      })
      .catch((err) => {
        if (cancelled) return;
        // C4: Postgres-level error — never show raw to users.
        setState({ loading: false, error: err?.message || "Couldn't load competitor search.", data: null });
      });
    return () => { cancelled = true; };
  }, [open, clientId, debouncedSearch, filters]);

  const handleToggleFilter = useCallback((paramKey, value) => {
    setFilters((prev) => ({ ...prev, [paramKey]: toggleInArray(prev[paramKey], value) }));
  }, []);

  // Two-layer write error handling per C4 / B5 §6.
  //   addCiCompetitor / removeCiCompetitor resolve with { success, error, message, ... }
  //   inside `data`. A Postgres-level error rejects (caught here → generic toast).
  //   A user-facing failure (data.success === false) resolves and we render
  //   data.error as-is — the backend writes user-facing copy.
  const runWrite = useCallback(async (atlasParkId, fn, successVerb) => {
    setWritePending((prev) => ({ ...prev, [atlasParkId]: true }));
    setWriteToast(null);
    setLimitReached(null);
    try {
      const resp = await fn();
      // Write RPC envelope: data is { success, error, message, ...extras }.
      const payload = resp?.data ?? {};
      if (!payload.success) {
        // User-facing failure — render data.error verbatim per B5 §6.
        setWriteToast({ kind: "error", text: payload.error || payload.message || "Couldn't update competitor roster." });
        // B5 §6: `limit_reached: true` arrives with `upgrade_options` (upsell).
        // Surface them as an inline notice above the toast so the client sees
        // the path to a higher plan.
        if (payload.limit_reached) {
          setLimitReached({
            errorText: payload.error || payload.message,
            upgradeOptions: Array.isArray(payload.upgrade_options) ? payload.upgrade_options : [],
          });
        }
      } else {
        setWriteToast({ kind: "success", text: payload.message || successVerb });
        // Flip the row's roster membership immediately for snappy UX, then
        // re-fetch the authoritative roster in the background.
        setRosterIds((prev) => {
          const next = new Set(prev);
          if (fn === addCiCompetitor) next.add(atlasParkId);
          else next.delete(atlasParkId);
          return next;
        });
        refreshRoster().then(() => onRosterChanged?.());
      }
    } catch (err) {
      // Postgres-level error — generic message, never raw (C4).
      setWriteToast({ kind: "error", text: "Couldn't update competitor roster. Please try again." });
    } finally {
      setWritePending((prev) => {
        const next = { ...prev };
        delete next[atlasParkId];
        return next;
      });
    }
  }, [refreshRoster, onRosterChanged]);

  const handleAdd = useCallback((result) => {
    const atlasParkId = result.atlas_park_id;
    if (!atlasParkId) return;
    runWrite(atlasParkId, () => addCiCompetitor(clientId, { atlasParkId }), "Competitor added.");
  }, [clientId, runWrite]);

  const handleRemove = useCallback((result) => {
    const atlasParkId = result.atlas_park_id;
    if (!atlasParkId) return;
    // B5 §5 / verification: "Remove competitor works with reason prompt".
    // Use the browser prompt as a lightweight reason capture — the picker is
    // a transient drawer and a full modal here would be heavier than the
    // interaction warrants. The reason is optional; cancelling the prompt
    // aborts the removal. B5 §6: removal below min_required is blocked
    // server-side and returns a user-facing error via data.error.
    let reason = null;
    try {
      const entered = window.prompt(
        `Remove ${result.name || result.competitor_name || "this competitor"} from your roster?\n\nOptional: add a reason (shown in your change history).`,
        ""
      );
      if (entered === null) return; // user cancelled
      reason = entered.trim() || null;
    } catch {
      // window.prompt can be unavailable in some sandboxed environments —
      // fall through with a null reason rather than blocking removal.
      reason = null;
    }
    runWrite(atlasParkId, () => removeCiCompetitor(clientId, { atlasParkId, reason }), "Competitor removed.");
  }, [clientId, runWrite]);

  const results = useMemo(() => {
    const list = state.data?.results;
    return Array.isArray(list) ? list : [];
  }, [state.data]);

  const facets = state.data?.facets || {};
  const noResults = !!state.data?.no_results || (results.length === 0 && !state.loading);
  const resultCount = state.data?.result_count ?? results.length;
  // config.client_can_manage gates whether add/remove controls render at all
  // (B5 §6). When false we hide the buttons entirely — the search still works
  // as a browse view.
  const clientCanManage = state.data?.config?.client_can_manage !== false;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Add competitors">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close competitor picker"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
        tabIndex={-1}
      />

      {/* Panel — bottom-sheet on phone, side drawer on tablet+ (house responsive shell) */}
      <div
        ref={containerRef}
        className="fixed inset-x-0 bottom-0 top-0 md:inset-y-0 md:right-0 md:left-auto w-full md:w-[640px] md:max-w-[calc(100vw-60px)] bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Add competitors</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Search the atlas for parks to track. Everything shows by default — filter to narrow.
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* B5 §7 — settings panel (plan limits, upgrade options, change history) */}
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Competitor settings"
              title="Settings"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search input */}
        <div className="px-4 md:px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, city, or park…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40 focus:border-[#32B1FF]"
            />
          </div>
        </div>

        {/* Filter chips — scrollable so a long facet list doesn't push results off-screen */}
        {state.loading && !state.data ? null : (
          <div className="px-4 md:px-5 py-3 border-b border-gray-200 dark:border-gray-800 space-y-3 max-h-[40vh] overflow-y-auto">
            {FACET_GROUPS.map((group) => (
              <FacetChips
                key={group.facetKey}
                group={group}
                values={facets[group.facetKey]}
                selected={filters[group.paramKey]}
                onToggle={handleToggleFilter}
                disabled={state.loading}
              />
            ))}
            {FACET_GROUPS.every((g) => !Array.isArray(facets[g.facetKey]) || facets[g.facetKey].length === 0) && (
              <div className="text-xs text-gray-400 dark:text-gray-500">
                No filter facets available for this search.
              </div>
            )}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-4 md:px-5 py-3">
          {state.loading && !state.data ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : state.error ? (
            <ErrorState variant="inset" padding="py-4" message={state.error} />
          ) : noResults ? (
            <div className="py-4 space-y-4">
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                <div className="font-medium text-gray-700 dark:text-gray-200">No competitors found</div>
                <div className="text-xs">
                  Try a broader search or fewer filters, or request tracking for a property not yet in the atlas.
                </div>
              </div>
              {/* B5 §4 — request form for unlisted properties */}
              <RequestForm
                clientId={clientId}
                searchTerm={debouncedSearch}
                clientCanManage={clientCanManage}
              />
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {resultCount} {resultCount === 1 ? "result" : "results"}
                {rosterLoading && " · loading your roster…"}
              </div>
              <ul>
                {results.filter((r) => !r.is_temporarily_closed).map((result) => {
                  const atlasParkId = result.atlas_park_id;
                  const onRoster = atlasParkId && rosterIds.has(atlasParkId);
                  return (
                    <ResultRow
                      key={atlasParkId || result.name}
                      result={result}
                      onRoster={onRoster}
                      onAdd={handleAdd}
                      onRemove={handleRemove}
                      writePending={!!writePending[atlasParkId]}
                      clientCanManage={clientCanManage}
                    />
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* B5 §6 — limit_reached upsell notice (shown above the toast) */}
        {limitReached && (
          <div className="px-4 md:px-5 py-2 border-t border-gray-200 dark:border-gray-800">
            <UpgradeNotice
              errorText={limitReached.errorText}
              upgradeOptions={limitReached.upgradeOptions}
              onViewSettings={() => setSettingsOpen(true)}
            />
          </div>
        )}

        {/* Write feedback toast — renders data.error verbatim on user-facing failures (B5 §6) */}
        {writeToast && (
          <div className="px-4 md:px-5 py-2 border-t border-gray-200 dark:border-gray-800">
            <div
              role="status"
              className={`text-xs rounded-lg px-3 py-2 ${
                writeToast.kind === "success"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                  : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
              }`}
            >
              {writeToast.text}
            </div>
          </div>
        )}

        {/* Hide controls when the plan doesn't allow self-serve management (B5 §6) */}
        {!clientCanManage && (
          <div className="px-4 md:px-5 py-2 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400">
            Your plan doesn't include self-serve competitor management. Contact your account manager to make changes.
          </div>
        )}
      </div>

      {/* B5 §7 — settings panel (plan limits, upgrade options, change history) */}
      <CompetitorSettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        clientId={clientId}
      />
    </div>
  );
};

export default CompetitorPicker;
